import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { cartItems } = useCart();
  const totalQty = cartItems.length;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products' },
    { name: 'Technical Details', path: '/technical' },
    { name: 'Projects', path: '/projects' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <div className={`text-2xl font-bold tracking-tight transition-colors duration-300 ${isScrolled ? 'text-[#111111]' : 'text-white'}`}>
              ROCK GRANITES
            </div>
            <div className={`hidden sm:flex items-center gap-1 border rounded px-2 py-0.5 transition-colors duration-300 ${isScrolled ? 'border-amber-500 bg-amber-50' : 'border-amber-400/70 bg-amber-400/10'}`}>
              <span className="text-amber-500 text-xs">★</span>
              <span className={`text-[10px] font-semibold tracking-wide uppercase leading-tight ${isScrolled ? 'text-amber-700' : 'text-amber-300'}`}>A Star Export House</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? isScrolled ? 'text-[#111111]' : 'text-white'
                    : isScrolled ? 'text-gray-600 hover:text-[#111111]' : 'text-gray-200 hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA + Cart */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link to="/cart" className={`relative p-2 transition-colors ${isScrolled ? 'text-gray-600 hover:text-[#111111]' : 'text-gray-200 hover:text-white'}`}>
              <ShoppingCart size={22} />
              {totalQty > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#111111] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {totalQty}
                </span>
              )}
            </Link>
            <Link to="/contact">
              <Button className={`transition-colors ${isScrolled ? 'bg-[#111111] text-white hover:bg-[#2a2a2a]' : 'bg-white text-[#111111] hover:bg-gray-100'}`}>
                Request Quote
              </Button>
            </Link>
          </div>

          {/* Mobile: Cart + Menu */}
          <div className="lg:hidden flex items-center gap-2">
            <Link to="/cart" className="relative p-2 text-gray-600 hover:text-[#111111]">
              <ShoppingCart size={22} />
              {totalQty > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#111111] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {totalQty}
                </span>
              )}
            </Link>
            <button
              className="p-2 text-[#111111]"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <nav className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block text-base font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-[#111111]'
                    : 'text-gray-600'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
              <Button className="w-full bg-[#111111] text-white hover:bg-[#2a2a2a]">
                Request Quote
              </Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;